import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { get } from "../lib/api";
import { useDebounced } from "../lib/hooks";
import { dueText } from "../lib/format";
import { resultPath, searchIcon, summaryText } from "../lib/reports";
import { todayISO } from "../../helpers/datetime";
import { NAV_PAGES } from "./nav";
import { useShell } from "./context";

// ⌘K palette: actions, pages, and a type-ahead over GET /v1/crm/search
// (people, companies, leads, signals; E9). "Search all results" opens /search.
export default function CommandPalette() {
    const shell = useShell();
    const history = useHistory();
    const [q, setQ] = useState("");
    const [active, setActive] = useState(0);
    const [results, setResults] = useState({ hits: [], due: [] });
    const dq = useDebounced(q.trim(), 180);
    const listRef = useRef(null);
    const open = shell.paletteOpen;

    useEffect(() => {
        if (open) {
            setQ("");
            setActive(0);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        const load = async () => {
            try {
                if (!dq) {
                    const due = await get("v1/crm/due", { limit: 5 });
                    if (!cancelled) setResults({ hits: [], due: due.data || [] });
                } else {
                    const res = await get("v1/crm/search", { q: dq, limit: 8 });
                    if (!cancelled) setResults({ hits: res.data || [], due: [] });
                }
            } catch (e) {
                if (!cancelled) setResults({ hits: [], due: [] });
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [dq, open]);

    const go = (path) => {
        shell.closePalette();
        history.push(path);
    };

    const items = useMemo(() => {
        const ql = q.trim().toLowerCase();
        const match = (s) => !ql || s.toLowerCase().includes(ql);
        const actions = [
            { group: "Actions", icon: "ri-chat-new-line", label: "Log a touchpoint", hint: "L", run: () => { shell.closePalette(); shell.openLog(); } },
            { group: "Actions", icon: "ri-sticky-note-line", label: "Today's note", run: () => go(`/notes/${todayISO()}`) },
            { group: "Actions", icon: "ri-user-add-line", label: "New person", run: () => { shell.closePalette(); shell.openCreate("contact"); } },
            { group: "Actions", icon: "ri-building-line", label: "New company", run: () => { shell.closePalette(); shell.openCreate("company"); } },
            { group: "Actions", icon: "ri-funds-line", label: "New lead", run: () => { shell.closePalette(); shell.openLead(); } },
            { group: "Actions", icon: "ri-radar-line", label: "Add signal", run: () => { shell.closePalette(); shell.openSignal(); } },
            { group: "Actions", icon: "ri-linkedin-box-line", label: "Import LinkedIn export", run: () => go("/crm/imports") },
            { group: "Actions", icon: "ri-git-merge-line", label: "Review duplicates", run: () => go("/crm/imports/duplicates") },
        ].filter((a) => match(a.label));
        const due = results.due.map((d) => ({
            group: "Needs attention",
            icon: d.kind === "company" ? "ri-building-line" : "ri-user-line",
            label: d.name,
            hint: dueText(d.due_in_days),
            run: () => go(d.kind === "company" ? `/crm/companies/${d.id}` : `/crm/contacts/${d.id}`),
        }));
        const hits = results.hits.map((r) => ({
            group: "Results",
            icon: searchIcon(r.type),
            label: r.title,
            hint: r.hidden_at ? `${summaryText(r.summary)} · hidden` : summaryText(r.summary),
            run: () => go(resultPath(r)),
        }));
        const all = q.trim()
            ? [{ group: "Results", icon: "ri-search-line", label: `Search all results for "${q.trim()}"`, hint: "with filters", run: () => go(`/search?q=${encodeURIComponent(q.trim())}`) }]
            : [];
        const pages = NAV_PAGES.filter((p) => match(p.label) || match(p.group || "")).map((p) => ({
            group: "Go to",
            icon: p.icon,
            label: p.key === "notes" ? "Go to Daily notes" : p.group ? `${p.group}: ${p.label}` : p.label,
            run: () => go(p.path),
        }));
        return ql ? [...hits, ...all, ...actions, ...pages] : [...actions, ...due, ...pages];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, results]);

    useEffect(() => {
        if (active >= items.length) setActive(Math.max(0, items.length - 1));
    }, [items, active]);

    useEffect(() => {
        const el = listRef.current && listRef.current.querySelector(".hz-palette-item.active");
        if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
    }, [active]);

    if (!open) return null;

    const onKey = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, items.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (items[active]) items[active].run();
        } else if (e.key === "Escape") {
            shell.closePalette();
        }
    };

    let lastGroup = null;
    return (
        <div className="hz-palette-backdrop" onMouseDown={(e) => e.target === e.currentTarget && shell.closePalette()}>
            <div className="hz-palette" role="dialog" aria-label="Command palette">
                <div className="hz-palette-input">
                    <i className="ri-search-line hz-muted" style={{ fontSize: 18 }}></i>
                    <input
                        autoFocus
                        value={q}
                        onChange={(e) => {
                            setQ(e.target.value);
                            setActive(0);
                        }}
                        onKeyDown={onKey}
                        placeholder="Search people, companies, leads, signals, or type a command…"
                    />
                    <span className="hz-kbd">esc</span>
                </div>
                <div className="hz-palette-list" ref={listRef}>
                    {items.length === 0 && <div className="hz-empty">{q.trim() !== dq ? "Searching…" : "No matches."}</div>}
                    {items.map((it, i) => {
                        const head = it.group !== lastGroup;
                        lastGroup = it.group;
                        return (
                            <React.Fragment key={it.group + i}>
                                {head && <div className="hz-palette-group">{it.group}</div>}
                                <div className={"hz-palette-item" + (i === active ? " active" : "")} onMouseEnter={() => setActive(i)} onClick={it.run}>
                                    <i className={it.icon}></i>
                                    <span className="hz-spacer hz-ellipsis">{it.label}</span>
                                    {it.hint && <span className="hz-small hz-muted hz-nowrap hz-palette-hint">{it.hint}</span>}
                                </div>
                            </React.Fragment>
                        );
                    })}
                </div>
                <div className="hz-palette-foot">
                    <span>↑↓ navigate</span>
                    <span>↵ open</span>
                    <span>L log a touch</span>
                </div>
            </div>
        </div>
    );
}
